import React from 'react';
import { borderColor, fontWeight, styled } from '@mui/system';
import { useTheme } from '@mui/material/styles';

const StyledBadge = styled('span')(({ theme, variant, size, borderColor }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
    height: 'auto',
    lineHeight: 1,
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeightMedium,
    ...badgeSizes(theme)[size],
    ...badgeVariants(theme)[variant],
    border: `1px solid ${borderColor || badgeVariants(theme)[variant]?.borderColor || theme.palette.border.secondary}`,
    color: badgeVariants(theme)[variant]?.textColor || theme.palette.text.primary,
}));

const SvgIcon = styled('span')(({ theme, variant, iconColor }) => ({
    display: 'flex',
    width: '12px',
    height: '12px',
    alignItems: 'center',
    marginRight: theme.customSpacing.xxs,
    color: iconColor || badgeVariants(theme)[variant]?.iconColor || theme.palette.foreground.primary,
    '& svg': {
        width: '12px',
        height: '12px',
    }
}));

const badgeSizes = (theme) => ({
    small: {
        padding: `${theme.customSpacing.xxs} ${theme.customSpacing.md}`,
        fontSize: theme.typography.textXS.fontSize,
        fontWeight: theme.typography.fontWeightRegular,
        lineHeight: theme.typography.textXS.lineHeight,
    },
    medium: {
        padding: `${theme.customSpacing.xxs} 10px`,
        fontSize: theme.typography.textSM.fontSize,
        lineHeight: theme.typography.textSM.lineHeight,
    },
    large: {
        padding: `${theme.customSpacing.xs} ${theme.customSpacing.lg}`,
        fontSize: theme.typography.textSM.fontSize,
        lineHeight: theme.typography.textSM.lineHeight,
    },
});

const badgeVariants = (theme) => ({
    pill: {
        borderRadius: theme.shape.radius.full,
        borderColor: theme.palette.border.secondary,
        background: theme.palette.background.secondary,
        textColor: theme.palette.text.disabled,
        iconColor: theme.palette.foreground.brand_primary,
    },
    pillTransparent: {
        borderRadius: theme.shape.radius.full,
        borderColor: theme.palette.border.tertiary,
        background: 'transparent',
        textColor: theme.palette.text.primary,
        iconColor: theme.palette.foreground.brand_primary,
    },
    pillError: {
        borderRadius: theme.shape.radius.full,
        borderColor: theme.palette.border.error,
        background: theme.palette.background.error_primary,
        textColor: theme.palette.text.error_primary,
        iconColor: theme.palette.foreground.error_primary,
    },
    pillWarning: {
        borderRadius: theme.shape.radius.full,
        borderColor: theme.palette.border.warning,
        background: theme.palette.background.warning_primary,
        textColor: theme.palette.text.warning_primary,
        iconColor: theme.palette.foreground.warning_primary,
    },
    pillSuccess: {
        borderRadius: theme.shape.radius.full,
        borderColor: theme.palette.border.success,
        background: theme.palette.background.success_primary,
        textColor: theme.palette.text.success_primary,
        iconColor: theme.palette.foreground.success_primary,
    },
});

const DefaultBadge = ({ children, variant = 'pill', startIcon = null, endIcon = null, size = 'medium', iconColor, borderColor, ...props }) => {
    const theme = useTheme();
    return (
        <StyledBadge variant={variant} size={size} borderColor={borderColor} {...props}>
            {startIcon && <SvgIcon variant={variant} iconColor={iconColor} >{startIcon}</SvgIcon>}
            {children}
            {endIcon && <SvgIcon variant={variant} iconColor={iconColor} >{endIcon}</SvgIcon>}
        </StyledBadge>
    );
};

export default DefaultBadge;
